import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {Home} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {PATHS} from 'routes/constants';
import {Button} from '@chekinapp/ui';
import {useKioskoStore} from 'context/kiosko';
import {useSummary} from 'context/summary';
import {usePath} from 'hooks';
import {useMutatePrintedCard} from 'hooks/useMutatePrintedCard';
import {StatusModal, StatusModalStates} from '@chekin/shared';
import {PrintedCardsModal} from '../PrintedCardsModal';
import {useTimeoutRef} from '@chekin/core';
import {useModalControls} from '@chekin/guestapp-ui-kit';

const TimeoutFinishProcess = 3000;

const getTitle = (state: StatusModalStates, t: TFunction) => {
  if (state === StatusModalStates.LOADING) {
    return t('printing_keys');
  } else if (state === StatusModalStates.ERROR) {
    return t('error_printing_keycard_title');
  } else {
    return t('your_keys_are_ready');
  }
};

const getText = (state: StatusModalStates, t: TFunction) => {
  if (state === StatusModalStates.LOADING) {
    return t('please_wait_while_we_re_printing_your_keycard');
  } else if (state === StatusModalStates.ERROR) {
    return t('error_printing_keycard_description');
  } else {
    return t('take_your_card_from_the_dispenser_below_the_screen');
  }
};

type Props = {
  onMaybeLater?: () => void;
  onGoToKeyCards?: () => void;
};
const PrintingCardsModal = ({onMaybeLater, onGoToKeyCards}: Props) => {
  const {t} = useTranslation('kiosko');
  const [printingState, setPrintingState] = useState<StatusModalStates>(
    StatusModalStates.IDLE,
  );
  const title = getTitle(printingState, t);
  const timeoutRef = useTimeoutRef();
  const setPrinterState = useKioskoStore(state => state.setPrinterState);
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const isPrinting = useKioskoStore(state => state.printer.isPrinting);
  const printed = useKioskoStore(state => state.cards.printed);
  const pending = useKioskoStore(state => state.cards.pending);
  const isPrintingSuccess = useKioskoStore(state => state.printer.isPrintingSuccess);
  const isPrintingError = useKioskoStore(state => state.printer.isPrintingError);
  const setCardsState = useKioskoStore(state => state.setCardsState);
  const resetPrinterState = useKioskoStore(state => state.resetPrinterState);
  const {reservationId} = useSummary();
  const {mutate: registerPrintedCard} = useMutatePrintedCard();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const printedRef = useRef(printed);
  const pendingRef = useRef(pending);

  useEffect(
    function updateRefValues() {
      printedRef.current = printed;
      pendingRef.current = pending;
    },
    [printed, pending],
  );

  const {
    openModal: openPrintingModal,
    closeModal: closePrintingModal,
    isOpen: isPrintingModalOpen,
  } = useModalControls();
  const {
    openModal: openPrintedCardsModal,
    closeModal: closePrintedCardsModal,
    isOpen: isPrintedCardsModalOpen,
  } = useModalControls();

  useEffect(
    function onPrintingStateChange() {
      if (isKiosko && isPrinting) {
        setPrintingState(StatusModalStates.LOADING);
        openPrintingModal();
      }
    },
    [isKiosko, isPrinting, openPrintingModal],
  );

  useEffect(
    function onPrintingSuccessStateChange() {
      if (isKiosko && isPrintingSuccess) {
        const currentPrinted = printedRef.current + 1;
        const currentPending = pendingRef.current - 1;

        setCardsState({printed: currentPrinted, pending: currentPending});
        setPrintingState(StatusModalStates.SUCCESS);
        setPrinterState({isPrintingSuccess: false});
        registerPrintedCard(reservationId);
      }
    },
    [
      isKiosko,
      isPrintingSuccess,
      setCardsState,
      setPrinterState,
      registerPrintedCard,
      reservationId,
    ],
  );

  useEffect(
    function onPrintingCardErrorStateChange() {
      if (isKiosko && isPrintingError) {
        setPrintingState(StatusModalStates.ERROR);
      }
    },
    [isKiosko, isPrintingError],
  );

  const finishProcess = useCallback(() => {
    setPrinterState({isPrinting: false, isPrintingSuccess: false});
    openPrintedCardsModal();
  }, [setPrinterState, openPrintedCardsModal]);

  const onFinishProcess = useCallback(() => {
    finishProcess();
  }, [finishProcess]);

  useEffect(
    function onPrintingSuccessStateChange() {
      if (printingState === StatusModalStates.SUCCESS) {
        timeoutRef.current = setTimeout(() => {
          onFinishProcess();
        }, TimeoutFinishProcess);
      }
    },
    [printingState, timeoutRef, onFinishProcess],
  );

  const handleCloseModals = useCallback(() => {
    closePrintedCardsModal();
    closePrintingModal();
  }, [closePrintedCardsModal, closePrintingModal]);

  const handleGoBackToHome = () => {
    navigate(getFullPath(PATHS.home));
    setPrintingState(StatusModalStates.IDLE);
    resetPrinterState();
  };

  return (
    <>
      <StatusModal
        state={printingState}
        open={isPrintingModalOpen}
        title={title}
        text={getText(printingState, t)}
        iconProps={{className: 'kiosko:w-[120px]'}}
      >
        {printingState === StatusModalStates.ERROR && (
          <Button onClick={handleGoBackToHome} className="!justify-center kiosko:text-kiosko-m kiosko:h-[112px] kiosko:w-[668px] kiosko:!rounded-[56px] kiosko:mt-14" variant="outline">
            <Home className="w-6 h-6 kiosko:w-12 kiosko:h-12 kiosko:mr-4" />
            {t('go_back_to_home')}
          </Button>
        )}
      </StatusModal>
      <PrintedCardsModal
        isOpen={isPrintedCardsModalOpen}
        onClose={closePrintedCardsModal}
        onMaybeLater={onMaybeLater ?? handleCloseModals}
        onGoToKeyCards={onGoToKeyCards ?? handleCloseModals}
      />
    </>
  );
};

const MemoizePrintingCard = memo(PrintingCardsModal);

export {MemoizePrintingCard as PrintingCardsModal};
